import { useCallback, useRef } from 'react'
import {
	Background,
	BackgroundVariant,
	MarkerType,
	Panel,
	ReactFlow,
	ReactFlowProvider,
	SelectionMode,
	reconnectEdge,
} from '@/libs/workflow-node-ui'
import type { Connection, FinalConnectionState } from '@/libs/workflow-node-ui'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { useSelector } from '@/libs/state'
import type { PhaseEdgeData } from '../application/workflow-canvas-layout'
import { useWorkflowCanvas } from '../hooks/use-workflow-canvas'
import { useWorkflowCanvasActions } from '../hooks/use-workflow-canvas-actions'
import { activeWorkflow$, canvasEdges$, editingPhaseId$, editingStepId$ } from '../store'
import { PhaseNode, StepNode } from './workflow-node'
import { WorkflowInfoNode } from './workflow-info-node'
import { WorkflowInfoEditSheet } from './workflow-info-edit-sheet'
import { PhaseCreateSheet } from './phase-create-sheet'
import { PhaseEditSheet } from './phase-edit-sheet'
import { StepCreateSheet } from './step-create-sheet'
import { StepEditSheet } from './step-edit-sheet'

const nodeTypes = { phaseNode: PhaseNode, stepNode: StepNode, workflowInfoNode: WorkflowInfoNode }

const defaultEdgeOptions = { animated: true, markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18 } }

const removeReactFlowAttribution = () => {
	document.querySelector('.react-flow__attribution')?.remove()
}

const CanvasInner = () => {
	const { nodes: rawNodes, edges, onNodesChange, initLayout, refreshLayout, resetPositions } = useWorkflowCanvas()
	const {
		handleAddPhase,
		handleAddStep,
		refreshWorkflow,
		handlePhaseConnect,
		handlePhaseDisconnect,
		handleTransitionConnect,
		handleTransitionDisconnect,
		handleDeletePhase,
		handleDeleteStep,
	} = useWorkflowCanvasActions({ initLayout, refreshLayout })
	const workflow = useSelector(activeWorkflow$)
	const reconnectSuccessful = useRef(false)

	const handleEditPhase = useCallback((phaseId: string) => {
		editingPhaseId$.set(phaseId)
	}, [])

	const handleEditStep = useCallback((stepId: string) => {
		editingStepId$.set(stepId)
	}, [])

	const nodes = rawNodes.map(node => {
		if (node.type === 'phaseNode') {
			return { ...node, data: { ...node.data, onEdit: handleEditPhase, onDelete: handleDeletePhase } }
		}
		if (node.type === 'stepNode') {
			return { ...node, data: { ...node.data, onEdit: handleEditStep, onDelete: handleDeleteStep } }
		}
		return node
	})

	const onConnect = useCallback(
		(connection: Connection) => {
			const { source, target, sourceHandle } = connection
			if (!source || !target) return
			const nodeMap = new Map(nodes.map(n => [n.id, n.type]))

			if (nodeMap.get(source) === 'phaseNode' && nodeMap.get(target) === 'phaseNode') {
				handlePhaseConnect(connection)
				return
			}

			if (sourceHandle?.startsWith('transition-source-') && nodeMap.get(target) === 'stepNode') {
				const transitionId = sourceHandle.replace('transition-source-', '')
				const targetStepId = target.replace('step-', '')
				handleTransitionConnect(transitionId, targetStepId)
			}
		},
		[nodes, handlePhaseConnect, handleTransitionConnect],
	)

	const onReconnectStart = useCallback(() => {
		reconnectSuccessful.current = false
	}, [])

	const onReconnect = useCallback(
		(oldEdge: Parameters<typeof reconnectEdge>[0], newConnection: Connection) => {
			reconnectSuccessful.current = true
			canvasEdges$.set(reconnectEdge(oldEdge, newConnection, canvasEdges$.get()))
			const { sourceHandle } = newConnection
			if (sourceHandle?.startsWith('transition-source-') && newConnection.target) {
				const transitionId = sourceHandle.replace('transition-source-', '')
				const targetStepId = newConnection.target.replace('step-', '')
				handleTransitionConnect(transitionId, targetStepId)
			} else if (oldEdge.id.startsWith('edge-phase-') && oldEdge.data && newConnection.target && workflow) {
				const d = oldEdge.data as PhaseEdgeData
				handlePhaseDisconnect({
					workflowId: workflow.id,
					sourcePhaseId: d.sourcePhaseId,
					slot: d.slot,
					targetPhaseId: d.targetPhaseId,
				})
				handlePhaseConnect(newConnection)
			}
		},
		[handleTransitionConnect, handlePhaseConnect, handlePhaseDisconnect, workflow],
	)

	const onReconnectEnd = useCallback(
		(_: MouseEvent | TouchEvent, edge: (typeof edges)[number], __: string, connectionState: FinalConnectionState) => {
			if (reconnectSuccessful.current || connectionState.isValid) return
			if (edge.id.startsWith('edge-transition-')) {
				handleTransitionDisconnect(edge.id)
			} else if (edge.id.startsWith('edge-phase-') && edge.data && workflow) {
				const d = edge.data as PhaseEdgeData
				handlePhaseDisconnect({
					workflowId: workflow.id,
					sourcePhaseId: d.sourcePhaseId,
					slot: d.slot,
					targetPhaseId: d.targetPhaseId,
				})
			}
		},
		[handleTransitionDisconnect, handlePhaseDisconnect, workflow],
	)

	return (
		<>
			<ReactFlow
				nodes={nodes}
				edges={edges}
				nodeTypes={nodeTypes}
				defaultEdgeOptions={defaultEdgeOptions}
				nodesDraggable={true}
				nodesConnectable={true}
				elementsSelectable={true}
				selectionOnDrag={true}
				selectionMode={SelectionMode.Partial}
				onNodesChange={onNodesChange}
				onConnect={onConnect}
				onReconnectStart={onReconnectStart}
				onReconnect={onReconnect}
				onReconnectEnd={onReconnectEnd}
				onInit={removeReactFlowAttribution}
				connectionRadius={40}
				fitView
				fitViewOptions={{ padding: 0.2 }}
			>
				<Background
					variant={BackgroundVariant.Dots}
					gap={12}
					size={1}
					patternClassName='bg-red-400'
					className='dark:[--xy-background-pattern-color-props:var(--color-stone-700)] [--xy-background-pattern-color-props:var(--color-stone-400)]'
				/>

				<Panel position='bottom-center'>
					<div className='flex items-center gap-2 bg-background border border-border shadow-md px-3 py-2'>
						<Button variant='ghost' size='sm' onClick={handleAddPhase} className='gap-1.5 text-sm'>
							<span className='text-base leading-none'>+</span>
							Fase
						</Button>
						<div className='w-px h-4 bg-border' />
						<Button variant='ghost' size='sm' onClick={handleAddStep} className='gap-1.5 text-sm'>
							<span className='text-base leading-none'>+</span>
							Passo
						</Button>
						<div className='w-px h-4 bg-border' />
						<Button variant='ghost' size='sm' onClick={resetPositions} className='gap-1.5 text-sm'>
							<Icons.GridView className='size-4' />
							Auto-layout
						</Button>
					</div>
				</Panel>
			</ReactFlow>

			<PhaseCreateSheet onSuccess={refreshWorkflow} />
			<PhaseEditSheet onSuccess={refreshWorkflow} />
			<StepCreateSheet onSuccess={refreshWorkflow} />
			<StepEditSheet onSuccess={refreshWorkflow} />
			<WorkflowInfoEditSheet onSuccess={refreshWorkflow} />
		</>
	)
}

const WorkflowEditorCanvas = () => (
	<div className='w-full h-full pb-8'>
		<ReactFlowProvider>
			<CanvasInner />
		</ReactFlowProvider>
	</div>
)

export { WorkflowEditorCanvas }
